import React, { FC, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { EvSection, IEvTabPanelProps, IEvTabProps } from '@evinced-private/ui-common';

import {
	DrilldownState,
	isValidReferrer
} from 'src/apps/dashboard/components/accessibility-summary/helpers/drilldownLinkHelper';
import {
	ALL_ISSUES_TABLE_ID,
	UNIQUE_ISSUES_TABLE_ID
} from 'src/apps/dashboard/constants/Dashboard';
import {
	MOBILE_FILTER_DEFINITIONS,
	MOBILE_FILTER_OPTIONS
} from 'src/apps/dashboard/constants/MobileDashboardDefaults';
import { buildLabelsExcludedFromDashboard } from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { useMobileDashboardDataQuery } from 'src/apps/dashboard/hooks/useMobileDashboardDataQuery';
import { useMobileScoreDataQuery } from 'src/apps/dashboard/hooks/useMobileScoreDataQuery';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { isConfigurationReady } from 'src/common/helpers/ConfigurationHelpers';
import { UNIQUE_QUERY_PARAM } from 'src/common/helpers/QueryParamsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { DASHBOARD_LABELS } from 'src/common/labels/dashboard.labels';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { renderIssuesTableContent } from './helpers/IssuesPageHelpers';
import {
	MOBILE_ALL_ISSUES_COLUMNS,
	MOBILE_UNIQUE_ISSUES_COLUMNS
} from './helpers/IssuesTableColumns';
import { useFiltersWithQueryParams } from './hooks/useFiltersWithQueryParams';
import { useMobileAllIssuesData } from './hooks/useMobileAllIssuesData';
import { useMobileUniqueIssuesData } from './hooks/useMobileUniqueIssuesData';
import { HEADER } from './MobileIssuesPage.constants';

export const MobileIssuesPage: FC = () => {
	const location = useLocation();
	const history = useHistory();
	const { getTableStateById } = useTableState();

	usePlatformConfigInitializer();

	const { configuration } = useConfiguration();
	const { isUniquenessServiceEnabled, coreLabels } = usePlatformConfigStore();
	const labelsExcludedFromDashboard = useMemo(
		() => buildLabelsExcludedFromDashboard(coreLabels),
		[coreLabels]
	);

	const { filters, handleFilterChange } = useFiltersWithQueryParams({
		filterDefinitions: MOBILE_FILTER_DEFINITIONS,
		filterOptions: MOBILE_FILTER_OPTIONS
	});

	const allTableState = getTableStateById(ALL_ISSUES_TABLE_ID);
	const uniqueTableState = getTableStateById(UNIQUE_ISSUES_TABLE_ID);

	const dashboardDataQuery = useMobileDashboardDataQuery(
		filters,
		MOBILE_FILTER_DEFINITIONS,
		labelsExcludedFromDashboard
	);
	const scoreDataQuery = useMobileScoreDataQuery(
		filters,
		MOBILE_FILTER_DEFINITIONS,
		labelsExcludedFromDashboard
	);

	const isUniqueFromUrl = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return searchParams.get(UNIQUE_QUERY_PARAM) === 'true';
	}, [location.search]);

	const activeTabIndex = isUniqueFromUrl ? 1 : 0;

	const {
		data: uniqueIssuesData,
		isFetching: isFetchingUnique,
		error: uniqueError
	} = useMobileUniqueIssuesData(
		filters,
		uniqueTableState.paginationInfo.page,
		uniqueTableState.paginationInfo.sizePerPage,
		uniqueTableState.tableSort?.[0],
		isUniqueFromUrl
	);

	const {
		data: allIssuesData,
		isFetching: isFetchingAll,
		error: allError
	} = useMobileAllIssuesData(
		filters,
		allTableState.paginationInfo.page,
		allTableState.paginationInfo.sizePerPage,
		allTableState.tableSort?.[0],
		!isUniqueFromUrl
	);

	const handleBackClick = useCallback((): void => {
		const { referrer } = (location.state as DrilldownState) ?? {};
		if (isValidReferrer(referrer)) {
			history.push(referrer);
		} else {
			history.push(RoutesHelper.getMobileAccessibilityDashboardPath());
		}
	}, [history, location.state]);

	const handleTabSelected = useCallback(
		(index: number) => {
			const searchParams = new URLSearchParams(location.search);
			if (index === 1) {
				searchParams.set(UNIQUE_QUERY_PARAM, 'true');
			} else {
				searchParams.delete(UNIQUE_QUERY_PARAM);
			}
			history.replace({
				pathname: location?.pathname,
				search: `?${searchParams.toString() ?? ''}`
			});
		},
		[history, location.pathname, location.search]
	);

	const { tabs, tabPanels } = useMemo(() => {
		const allTabContent = renderIssuesTableContent({
			columns: MOBILE_ALL_ISSUES_COLUMNS,
			isFetching: isFetchingAll,
			error: allError,
			data: allIssuesData,
			tableId: ALL_ISSUES_TABLE_ID,
			title: DASHBOARD_LABELS.ALL_ISSUES_TABLE_TITLE,
			errorMessage: DASHBOARD_LABELS.ALL_ISSUES_LOAD_ERROR
		});
		const uniqueTabContent = renderIssuesTableContent({
			columns: MOBILE_UNIQUE_ISSUES_COLUMNS,
			isFetching: isFetchingUnique,
			error: uniqueError,
			data: uniqueIssuesData,
			tableId: UNIQUE_ISSUES_TABLE_ID,
			title: DASHBOARD_LABELS.UNIQUE_ISSUES_TABLE_TITLE,
			errorMessage: DASHBOARD_LABELS.UNIQUE_ISSUES_LOAD_ERROR
		});

		const tabs: IEvTabProps[] = [
			{
				id: 'all-issues-tab',
				tablistId: 'issues-page-tabs',
				ref: null,
				isActive: activeTabIndex === 0,
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				handleKeyDown: () => {},
				children: DASHBOARD_LABELS.ALL_ISSUES_TAB,
				ariaControls: 'all-issues-panel',
				ariaLabel: DASHBOARD_LABELS.ALL_ISSUES_TABLE_ARIA_LABEL,
				onActivate: () => handleTabSelected(0),
				dataAutomationId: 'all-issues-tab'
			},
			{
				id: 'unique-issues-tab',
				tablistId: 'issues-page-tabs',
				ref: null,
				isActive: activeTabIndex === 1,
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				handleKeyDown: () => {},
				children: DASHBOARD_LABELS.UNIQUE_ISSUES_TAB,
				ariaControls: 'unique-issues-panel',
				ariaLabel: DASHBOARD_LABELS.UNIQUE_ISSUES_TABLE_ARIA_LABEL,
				onActivate: () => handleTabSelected(1),
				dataAutomationId: 'unique-issues-tab'
			}
		];

		const tabPanels: IEvTabPanelProps[] = [
			{
				id: 'all-issues-panel',
				ariaLabelledBy: 'issues-page-tabs-all-issues-tab',
				isActive: activeTabIndex === 0,
				children: (
					<EvSection
						ariaLabel={DASHBOARD_LABELS.ALL_ISSUES_TABLE_ARIA_LABEL}
						className="issues-table-section"
					>
						<div className="issues-table-container">{allTabContent}</div>
					</EvSection>
				)
			},
			{
				id: 'unique-issues-panel',
				ariaLabelledBy: 'issues-page-tabs-unique-issues-tab',
				isActive: activeTabIndex === 1,
				children: (
					<EvSection
						ariaLabel={DASHBOARD_LABELS.UNIQUE_ISSUES_TABLE_ARIA_LABEL}
						className="issues-table-section"
					>
						<div className="issues-table-container">{uniqueTabContent}</div>
					</EvSection>
				)
			}
		];

		return { tabs, tabPanels };
	}, [
		activeTabIndex,
		allError,
		allIssuesData,
		handleTabSelected,
		isFetchingAll,
		isFetchingUnique,
		uniqueError,
		uniqueIssuesData
	]);

	const header = useMemo(
		() => ({
			...HEADER,
			backLink: { ...HEADER.backLink, onClick: handleBackClick }
		}),
		[handleBackClick]
	);

	const filter = useMemo(
		() => ({
			filters,
			filterDefinitions: MOBILE_FILTER_DEFINITIONS,
			filterOptions: MOBILE_FILTER_OPTIONS,
			onFilterChange: handleFilterChange,
			filterPreset: 'issues' as const
		}),
		[filters, handleFilterChange]
	);

	const scoreQuery = useMemo(
		() => ({
			data: scoreDataQuery.data,
			isLoading: scoreDataQuery.isLoading,
			error: scoreDataQuery.error
		}),
		[scoreDataQuery.data, scoreDataQuery.isLoading, scoreDataQuery.error]
	);

	const dashboardQuery = useMemo(
		() => ({
			data: dashboardDataQuery.data,
			isLoading: dashboardDataQuery.isLoading,
			error: dashboardDataQuery.error
		}),
		[dashboardDataQuery.data, dashboardDataQuery.isLoading, dashboardDataQuery.error]
	);

	const bottom = useMemo(
		() => ({
			kind: 'tabs' as const,
			tabState: { tabs, tabPanels, activeTabIndex, onTabSelected: handleTabSelected }
		}),
		[tabs, tabPanels, activeTabIndex, handleTabSelected]
	);

	if (!isConfigurationReady(configuration)) {
		return null;
	}

	return (
		<DashboardTemplate
			header={header}
			filter={filter}
			effectiveFilters={filters}
			scoreQuery={scoreQuery}
			dashboardQuery={dashboardQuery}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			bottom={bottom}
		/>
	);
};
